import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Typography,
  Box,
} from "@mui/material";
import useAxios from "../../../../hooks/useAxios";
import "./CreateStudentModal.css";
import StyledTextField from "../../../common/StyledComponents/StyledTextField";

const CreateStudentModal = ({ open, onClose }) => {
  const [studentName, setStudentName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const axiosInstance = useAxios();

  const handleSubmit = async () => {
    if (!studentName.trim()) {
      setError("Student name is required");
      return;
    }

    setError(null);
    setSuccess(null);
    setLoading(true);

    try {
      const response = await axiosInstance.post("/student-accounts/create", {
        student_name: studentName.trim(),
      });

      if (response.data.success) {
        setSuccess("Student account created successfully!");
        setTimeout(() => { onClose(true); }, 2000); // Refresh table
      } else {
        // Handle specific error codes
        if (response.data.error_code === 'STUDENT_LIMIT_REACHED') {
          setError(
            <Box>
              <Typography variant="body2" color="error" gutterBottom>
                {response.data.message}
              </Typography>
              <Typography variant="body2" color="error">
                Please upgrade your plan to create more students.
              </Typography>
            </Box>
          );
        } else if (response.data.error_code === 'NO_SUBSCRIPTION') {
          setError(
            <Box>
              <Typography variant="body2" color="error" gutterBottom>
                {response.data.message}
              </Typography>
              <Typography variant="body2" color="error">
                Please purchase a subscription plan to create student accounts.
              </Typography>
            </Box>
          );
        } else {
          setError(response.data.message || "Failed to create student account.");
        }
      }
    } catch (err) {
      setError(err.response?.data?.message || "An unexpected error occurred.");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setStudentName("");
    setError(null);
    setSuccess(null);
    onClose(false);
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle className="modal-title">
        Create New Student Account
      </DialogTitle>
      
      <DialogContent>
        <Typography className="modal-description">
          Enter the student's name to create their account. The system will automatically generate:
        </Typography>
        
        <Box className="features-list">
          <Typography variant="body2" className="feature-item">
            • A unique login code for easy access
          </Typography>
          {/* <Typography variant="body2" className="feature-item">
            • Email and password credentials
          </Typography> */}
          <Typography variant="body2" className="feature-item">
            • Students can login using their login code immediately
          </Typography>
        </Box>

        <StyledTextField
          fullWidth
          label="Student Name"
          value={studentName}
          onChange={(e) => setStudentName(e.target.value)}
          margin="normal"
          error={!!error}
          helperText={error}
          placeholder="e.g., John Smith"
          autoFocus
        />

        {success && (
          <Typography variant="body2" color="success.main" className="success-message">
            {success}
          </Typography>
        )}
      </DialogContent>

      <DialogActions className="modal-actions">
        <Button 
          onClick={handleClose} 
          disabled={loading}
          className="secondary-button"
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={loading || !studentName.trim()}
          startIcon={loading && <CircularProgress size={20} />}
          className="primary-button"
        >
          {loading ? "Creating..." : "Create Student"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateStudentModal; 